'use client'

import {useEffect, useMemo, useState} from 'react'
import {Bot, Sparkles, Trash2} from 'lucide-react'
import type {Contact, GroupAutoReplyConfig, GroupKeywordReplyRule, ReplyPayload, SettingsState} from '@/types'
import {avatarDisplaySrc, displayName, emptyReplyPayload, filterContacts, payloadText} from '@/lib/utils'
import {createGroupKeywordRule, normalizeGroupAutoReplyConfig} from '@/lib/constants'
import {PageHeader} from '@/components/PageHeader'
import {Switch} from '@/components/Switch'
import {ReplyPayloadEditor} from '@/components/ReplyPayloadEditor'
import {message} from '@/components/Toast'

/**
 * Per-group auto-reply editor. Layout restyled from WechatBotConsole.jsx:
 * left rail with selectable groups, right column with AI prompt and keyword
 * rule cards. Keyword rules use the same composer-style editor as the
 * personal-level AutoReplyView.
 */

type RuleDraft = {
    id?: string
    keyword: string
    matchType: NonNullable<GroupKeywordReplyRule['matchType']>
    replyPayload: ReplyPayload
    enabled: boolean
}

const emptyRuleDraft = (): RuleDraft => ({
    keyword: '',
    matchType: 'exact',
    replyPayload: emptyReplyPayload(),
    enabled: true,
})

const matchTypeLabel = (value: string) => {
    if (value === 'exact') return '精准匹配'
    if (value === 'fuzzy') return '模糊匹配'
    if (value === 'regex') return '正则匹配'
    return value
}

export function GroupAutoReplyView({
    groups,
    settings,
    saveSettings,
}: {
    groups: Contact[]
    settings: SettingsState
    saveSettings: (patch: Partial<SettingsState>) => void
}) {
    const [selectedWxid, setSelectedWxid] = useState('')
    const [query, setQuery] = useState('')
    const [ruleDraft, setRuleDraft] = useState<RuleDraft>(emptyRuleDraft())
    const [ruleError, setRuleError] = useState('')
    const selected = groups.find((group) => group.wxid === selectedWxid) || groups[0] || null
    const savedConfig = selected ? settings.groupAutoReply[selected.wxid] : null
    const [draft, setDraft] = useState<GroupAutoReplyConfig>(() => normalizeGroupAutoReplyConfig())
    const filteredGroups = useMemo(() => filterContacts(groups, query), [groups, query])

    useEffect(() => {
        setDraft(normalizeGroupAutoReplyConfig(savedConfig))
        setRuleDraft(emptyRuleDraft())
        setRuleError('')
    }, [savedConfig, selected])

    const persist = (next: GroupAutoReplyConfig) => {
        if (!selected) return
        saveSettings({
            groupAutoReply: {
                ...settings.groupAutoReply,
                [selected.wxid]: next,
            },
        })
    }

    const updateDraft = (patch: Partial<GroupAutoReplyConfig>, persistNow = false) => {
        setDraft((prev) => {
            const next = {...prev, ...patch}
            if (persistNow && selected) {
                saveSettings({
                    groupAutoReply: {
                        ...settings.groupAutoReply,
                        [selected.wxid]: next,
                    },
                })
            }
            return next
        })
    }

    const savePrompt = () => persist(draft)

    const isEditingRule = ruleDraft.id !== undefined

    const resetRuleDraft = () => {
        setRuleDraft(emptyRuleDraft())
        setRuleError('')
    }

    const handleSaveRule = () => {
        const keyword = ruleDraft.keyword.trim()
        const reply = payloadText(ruleDraft.replyPayload).trim()
        if (!keyword) {
            setRuleError('请输入触发关键词')
            return
        }
        if (!reply) {
            setRuleError('请输入回复内容')
            return
        }
        let nextRules: GroupKeywordReplyRule[]
        if (ruleDraft.id) {
            nextRules = draft.keywordRules.map((rule) =>
                rule.id === ruleDraft.id
                    ? {
                          ...rule,
                          keyword,
                          reply,
                          matchType: ruleDraft.matchType,
                          enabled: ruleDraft.enabled,
                          replyType: ruleDraft.replyPayload.type || 'text',
                          replyPayload: ruleDraft.replyPayload,
                      }
                    : rule,
            )
        } else {
            const newRule = createGroupKeywordRule()
            newRule.keyword = keyword
            newRule.reply = reply
            newRule.matchType = ruleDraft.matchType
            newRule.enabled = ruleDraft.enabled
            newRule.replyType = ruleDraft.replyPayload.type || 'text'
            newRule.replyPayload = ruleDraft.replyPayload
            nextRules = [...draft.keywordRules, newRule]
        }
        const next = {...draft, keywordRules: nextRules}
        setDraft(next)
        persist(next)
        resetRuleDraft()
    }

    const handleEditRule = (rule: GroupKeywordReplyRule) => {
        setRuleDraft({
            id: rule.id,
            keyword: rule.keyword,
            matchType:
                rule.matchType === 'fuzzy' || rule.matchType === 'regex' ? rule.matchType : 'exact',
            replyPayload: rule.replyPayload || {type: rule.replyType || 'text', content: rule.reply},
            enabled: rule.enabled,
        })
        setRuleError('')
    }

    const handleToggleRule = (rule: GroupKeywordReplyRule) => {
        const next = {
            ...draft,
            keywordRules: draft.keywordRules.map((item) =>
                item.id === rule.id ? {...item, enabled: !item.enabled} : item,
            ),
        }
        setDraft(next)
        persist(next)
    }

    const handleRemoveRule = (rule: GroupKeywordReplyRule) => {
        const next = {
            ...draft,
            keywordRules: draft.keywordRules.filter((item) => item.id !== rule.id),
        }
        setDraft(next)
        persist(next)
        if (ruleDraft.id === rule.id) {
            resetRuleDraft()
        }
    }

    return (
        <section className="flex flex-col h-full bg-page">
            <PageHeader title="群自动回复"/>
            <div className="flex-1 flex flex-col md:flex-row overflow-y-auto md:overflow-hidden p-3 md:p-6 gap-3 md:gap-6">
                {/* Left — group picker */}
                <div className="w-full md:w-[298px] shrink-0 bg-white rounded-card shadow-card border border-hair flex flex-col overflow-hidden h-auto md:h-full">
                    <div className="p-4 border-b border-hair bg-gray-50 font-medium text-ink shrink-0">选择群聊</div>
                    <div className="p-3 border-b border-hair bg-page shrink-0">
                        <input
                            type="text"
                            value={query}
                            onChange={(event) => setQuery(event.target.value)}
                            placeholder="搜索群聊"
                            className="w-full h-8 bg-white border border-hair rounded px-3 text-sm text-ink placeholder:text-muted focus:outline-none focus:border-accent focus:ring-1 focus:ring-accent/30"
                        />
                    </div>
                    <div className="flex-1 overflow-y-auto">
                        {filteredGroups.length ? (
                            filteredGroups.map((group) => {
                                const isActive = selected?.wxid === group.wxid
                                return (
                                    <button
                                        type="button"
                                        key={group.wxid}
                                        onClick={() => setSelectedWxid(group.wxid)}
                                        className={`flex items-center gap-3 p-3 w-full text-left border-b border-hair/40 transition-colors ${
                                            isActive ? 'bg-subActive text-ink' : 'text-soft hover:bg-subHover'
                                        }`}
                                    >
                                        <img
                                            src={avatarDisplaySrc(group.avatar, group.wxid)}
                                            alt={displayName(group)}
                                            className="w-10 h-10 rounded bg-gray-100 shrink-0"
                                        />
                                        <span className="text-sm truncate">{displayName(group)}</span>
                                    </button>
                                )
                            })
                        ) : (
                            <div className="text-center text-sm text-muted py-10">
                                {query ? '没有匹配的群聊' : '暂无群聊'}
                            </div>
                        )}
                    </div>
                </div>

                {/* Right — per-group config */}
                <div className="flex-1 overflow-y-auto h-full pr-2">
                    {selected ? (
                        <div className="max-w-4xl space-y-6 pb-6">
                            <div className="grid grid-cols-1 gap-6">
                                <AiPromptCard
                                    enabled={draft.aiEnabled}
                                    prompt={draft.prompt}
                                    onToggle={() => {
                                        if (!draft.aiEnabled && !settings.aiConfig?.apiKey) {
                                            message.warning('请先在「系统设置 → AI 大模型配置」中配置 API Key')
                                            return
                                        }
                                        updateDraft({aiEnabled: !draft.aiEnabled}, true)
                                    }}
                                    onPromptChange={(value) => setDraft({...draft, prompt: value})}
                                    onSave={savePrompt}
                                />
                                <KeywordCard
                                    enabled={draft.keywordEnabled}
                                    isEditing={isEditingRule}
                                    rules={draft.keywordRules}
                                    ruleDraft={ruleDraft}
                                    setRuleDraft={setRuleDraft}
                                    resetRuleDraft={resetRuleDraft}
                                    ruleError={ruleError}
                                    onToggle={() =>
                                        updateDraft({keywordEnabled: !draft.keywordEnabled}, true)
                                    }
                                    onSave={handleSaveRule}
                                    onEditRule={handleEditRule}
                                    onToggleRule={handleToggleRule}
                                    onRemoveRule={handleRemoveRule}
                                    voiceId={settings.tts?.selectedVoiceId}
                                />
                            </div>
                        </div>
                    ) : (
                        <div className="flex h-full items-center justify-center text-sm text-muted">
                            请选择一个群聊
                        </div>
                    )}
                </div>
            </div>
        </section>
    )
}

function AiPromptCard({
    enabled,
    prompt,
    onToggle,
    onPromptChange,
    onSave,
}: {
    enabled: boolean
    prompt: string
    onToggle: () => void
    onPromptChange: (value: string) => void
    onSave: () => void
}) {
    return (
        <div className="bg-white rounded-lg shadow-card border border-hair p-6 flex flex-col">
            <div className="flex justify-between items-center mb-4 pb-4 border-b border-hair">
                <div className="flex items-center gap-2">
                    <Sparkles size={18} className="text-accent"/>
                    <h3 className="font-medium text-ink">AI 智能回复</h3>
                </div>
                <Switch checked={enabled} onChange={onToggle}/>
            </div>
            <div className={`flex flex-col ${enabled ? '' : 'opacity-50 pointer-events-none'}`}>
                <label className="text-sm text-soft mb-2">群聊专属 AI 提示词 (Prompt)：</label>
                <textarea
                    className="w-full border border-hair rounded-md p-3 bg-page focus:outline-none focus:bg-white focus:border-accent resize-none text-sm text-soft"
                    rows={4}
                    value={prompt}
                    onChange={(event) => onPromptChange(event.target.value)}
                    placeholder="请输入该群专属的 AI 人设和回复规则..."
                />
                <div className="mt-4 flex justify-end">
                    <button
                        type="button"
                        onClick={onSave}
                        className="bg-accent hover:bg-accent-hover text-white px-8 py-2.5 rounded shadow-card transition-colors text-sm font-medium"
                    >
                        保存 Prompt
                    </button>
                </div>
            </div>
        </div>
    )
}

function KeywordCard({
    enabled,
    isEditing,
    rules,
    ruleDraft,
    setRuleDraft,
    resetRuleDraft,
    ruleError,
    onToggle,
    onSave,
    onEditRule,
    onToggleRule,
    onRemoveRule,
    voiceId,
}: {
    enabled: boolean
    isEditing: boolean
    rules: GroupKeywordReplyRule[]
    ruleDraft: RuleDraft
    setRuleDraft: (draft: RuleDraft) => void
    resetRuleDraft: () => void
    ruleError: string
    onToggle: () => void
    onSave: () => void
    onEditRule: (rule: GroupKeywordReplyRule) => void
    onToggleRule: (rule: GroupKeywordReplyRule) => void
    onRemoveRule: (rule: GroupKeywordReplyRule) => void
    voiceId?: string
}) {
    return (
        <div className="bg-white rounded-lg shadow-card border border-hair p-6 flex flex-col">
            <div className="flex justify-between items-center mb-4 pb-4 border-b border-hair">
                <div className="flex items-center gap-2">
                    <Bot size={18} className="text-accent"/>
                    <h3 className="font-medium text-ink">群专属关键词回复 (优先)</h3>
                </div>
                <div className="flex items-center gap-3">
                    {isEditing ? (
                        <button
                            type="button"
                            className="text-xs text-muted hover:text-ink"
                            onClick={resetRuleDraft}
                        >
                            取消编辑
                        </button>
                    ) : null}
                    <Switch checked={enabled} onChange={onToggle}/>
                </div>
            </div>
            <div
                className={`flex flex-col overflow-hidden ${
                    enabled ? '' : 'opacity-50 pointer-events-none'
                }`}
            >
                <div className="flex flex-col bg-page p-5 rounded-lg border border-hair shrink-0 mb-5 shadow-card">
                    <div className="flex gap-4 mb-4">
                        <select
                            value={ruleDraft.matchType}
                            onChange={(event) =>
                                setRuleDraft({
                                    ...ruleDraft,
                                    matchType: event.target.value as RuleDraft['matchType'],
                                })
                            }
                            className="w-32 h-10 border border-hair rounded-md px-3 text-sm text-ink focus:outline-none focus:border-accent bg-white cursor-pointer"
                        >
                            <option value="exact">精准匹配</option>
                            <option value="fuzzy">包含 (模糊)</option>
                            <option value="regex">正则匹配</option>
                        </select>
                        <input
                            type="text"
                            placeholder="触发关键词 (多个词可用 | 分隔)"
                            value={ruleDraft.keyword}
                            onChange={(event) => setRuleDraft({...ruleDraft, keyword: event.target.value})}
                            className="flex-1 h-10 border border-hair rounded-md px-3 text-sm text-ink placeholder:text-muted focus:outline-none focus:border-accent bg-white"
                        />
                    </div>
                    <div className="bg-white border border-hair rounded-md px-4 py-3 relative flex flex-col">
                        <ReplyPayloadEditor
                            payload={ruleDraft.replyPayload}
                            onChange={(replyPayload) => setRuleDraft({...ruleDraft, replyPayload})}
                            placeholder="回复文本内容... (支持与图片、文件、语音混合发送)"
                            voiceId={voiceId}
                            action={
                                <button
                                    type="button"
                                    onClick={onSave}
                                    className="bg-accent hover:bg-accent-hover text-white px-6 h-9 rounded-md text-sm transition-colors font-medium shadow-card"
                                >
                                    {isEditing ? '保存修改' : '添加群规则'}
                                </button>
                            }
                        />
                    </div>
                    {ruleError ? (
                        <div className="mt-3 px-3 py-2 rounded bg-red-50 border border-red-100 text-xs text-red-600">
                            {ruleError}
                        </div>
                    ) : null}
                </div>

                <div className="flex items-center justify-between mb-2">
                    <div className="text-xs text-muted">已添加关键词 ({rules.length})</div>
                </div>
                <div className="flex-1 overflow-y-auto space-y-3 pr-1 max-h-[400px]">
                    {rules.length === 0 ? (
                        <div className="text-center text-muted text-sm mt-10 mb-10">
                            暂无群专属关键词
                        </div>
                    ) : (
                        rules.map((rule) => {
                            const rowEditing = ruleDraft.id === rule.id
                            const replyText = rule.reply || payloadText(rule.replyPayload || {
                                type: rule.replyType || 'text',
                                content: rule.reply,
                            })
                            return (
                                <div
                                    key={rule.id}
                                    className={`flex justify-between items-start bg-white p-4 rounded-lg border transition-all group ${
                                        rowEditing
                                            ? 'border-accent bg-green-50/40 shadow-card'
                                            : 'border-hair hover:border-accent hover:shadow-card'
                                    }`}
                                    onClick={() => onEditRule(rule)}
                                >
                                    <div className="flex-1 overflow-hidden pr-4">
                                        <div className="flex items-center mb-2">
                                            <span className="text-sm font-medium text-ink mr-3">
                                                {rule.keyword}
                                            </span>
                                            <span className="text-[11px] bg-accent/10 text-accent px-2 py-0.5 rounded border border-accent/20">
                                                {matchTypeLabel(rule.matchType || 'exact')}
                                            </span>
                                        </div>
                                        {replyText ? (
                                            <div className="text-[13px] text-soft break-words leading-relaxed">
                                                {replyText}
                                            </div>
                                        ) : null}
                                    </div>
                                    <div className="flex items-center gap-1 shrink-0">
                                        <Switch
                                            checked={rule.enabled}
                                            onChange={() => onToggleRule(rule)}
                                        />
                                        <button
                                            type="button"
                                            title="删除"
                                            className="p-2 rounded text-muted hover:bg-red-50 hover:text-red-500"
                                            onClick={(event) => {
                                                event.stopPropagation()
                                                onRemoveRule(rule)
                                            }}
                                        >
                                            <Trash2 size={16}/>
                                        </button>
                                    </div>
                                </div>
                            )
                        })
                    )}
                </div>
            </div>
        </div>
    )
}
